/**
 * Programs: 4- and 6-week plans that tie a weekly camera check, rest breaks
 * through the working day and short exercise snacks together
 * (docs/PRODUCT.md §12). Written the way a physical therapist lays out a home
 * program for a desk worker, in the app's wellness wording (LEGAL §4): habits,
 * awareness and what the app measures — never pain, treatment or outcomes.
 *
 * Structure and where the numbers come from (sources list, features/sources):
 * - Breaks every 30–50 min of continuous sitting, 2–10 min long; get up at
 *   least hourly; shift position often between breaks (~20–30 times an hour).
 *   dunstan-2012, cornell-20-8-2, postural-shift-2023, waongenngarm-2021,
 *   office-msd-2022.
 * - Active breaks (targeted stretches, deep neck flexor and shoulder-blade
 *   work, brief moderate bouts) over passive rest; exercise snacks of a few
 *   minutes. exercise-snacks-2026, active-break-2024.
 * - Kept up for at least 5–6 weeks: the 6-week program. The 4-week one is the
 *   starter.
 *
 * Rules the content follows (tests/programs.test.ts enforces them):
 * - Each week: 7 days, exactly one rest day (no snacks; breaks still apply),
 *   at least one camera check. Week 1 day 1 is the baseline check and the last
 *   day of the program is the final check; the rest are weekly checks on day 7.
 * - Breaks within 30–50 min / 2–10 min. Snacks 2–5 min, at most 3 moves
 *   (the same ceiling as MAX_RECOMMENDED_EXERCISES), 1–3 snacks a day.
 * - Workday snacks are doable at a desk, a doorway or a wall in office
 *   clothes. Floor moves (lying, kneeling, all fours) only appear in the
 *   evening home snack, whose id starts with `home-`.
 */
/**
 * Every planned check is 15 minutes (2026-10-17): short enough to start on a
 * busy day, and the same length every week so the weeks compare fairly —
 * posture drifts as sitting goes on, so a 30-minute check is not comparable
 * with a 15-minute one. Users can still pick 30 for an extra check.
 */
export const PROGRAM_CHECK_MINUTES = 15;
const DAYS_PER_WEEK = 7;
/** Day row shorthand. `rest` days get no snacks by construction. */
const day = (n, snackIds, opts = {}) => ({
    day: n,
    check: opts.check ?? false,
    snackIds: opts.rest ? [] : snackIds,
    rest: opts.rest ?? false,
});
const STOP_LINE = 'Stop if anything hurts and talk to a physical therapist or doctor.';
// ---------------------------------------------------------------------------
// Desk Reset — 4 weeks
// ---------------------------------------------------------------------------
const DESK_RESET = {
    id: 'desk-reset-4w',
    name: 'Desk Reset',
    weeks: 4,
    summary: 'Four weeks to build a break habit and learn the basic moves that go with it.',
    forWhom: 'Anyone new to posture habits who sits at a desk for most of the day and wants a simple routine to start with.',
    commitment: [
        'A 15-minute camera check once a week',
        'A short break every 30–45 min of sitting',
        '1–3 exercise snacks a day, 2–5 min each',
        'One rest day a week from snacks (breaks still count)',
    ],
    rationale: 'Long stretches of unbroken sitting are the habit this program works on. Desk-work guidance recommends ' +
        'breaking up sitting every 30–50 minutes, so the break interval starts at 45 minutes and tightens to 30 ' +
        'as the habit settles. Breaks work better when you do something in them: reviews of exercise snacks ' +
        'favour short, targeted moves over just sitting back, so most breaks carry a 2–5 minute snack. The ' +
        'weeks move from loosening up, to waking up the neck and shoulder-blade muscles, to holding a tall ' +
        'position longer, to fitting it all into a normal workday. A weekly camera check shows how your ' +
        'sitting changes while you build the habit. ' +
        STOP_LINE,
    sourceIds: [
        'dunstan-2012',
        'cornell-20-8-2',
        'exercise-snacks-2026',
        'active-break-2024',
        'waongenngarm-2021',
        'postural-shift-2023',
        'office-msd-2022',
    ],
    plan: [
        {
            week: 1,
            theme: 'Loosen up',
            goal: 'Take a break every 45 minutes and learn three easy loosening moves.',
            why: 'Starting with gentle range-of-motion moves keeps the first week easy, so the part that matters most ' +
                'this week, getting up on time, has room to become a habit.',
            whatToNotice: 'Your report will likely show breaks taken on schedule. Today’s baseline check is your starting point for every week after.',
            postureCue: 'Ears over shoulders, chin level.',
            breaks: {
                everyMin: 45,
                minutes: 2,
                note: 'Stand up and walk for 2 minutes every 45 minutes of sitting. Between breaks, shift how you sit every ' +
                    'few minutes. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'dr1-loosen',
                    title: 'Neck and shoulder loosener',
                    exerciseIds: ['shoulder-rolls', 'neck-range', 'chin-tuck'],
                    minutes: 3,
                },
                {
                    id: 'dr1-open',
                    title: 'Chest and upper-back opener',
                    exerciseIds: ['doorway-pec-stretch', 'seated-thoracic-extension', 'upper-trap-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-dr1',
                    title: 'Evening home snack: floor unwind',
                    exerciseIds: ['chin-tuck-supine', 'open-book'],
                    minutes: 4,
                },
            ],
            days: [
                day(1, ['dr1-loosen'], { check: true }),
                day(2, ['dr1-loosen', 'dr1-open']),
                day(3, ['dr1-loosen', 'dr1-open']),
                day(4, [], { rest: true }),
                day(5, ['dr1-loosen', 'dr1-open']),
                day(6, ['dr1-loosen', 'home-dr1']),
                day(7, ['dr1-loosen', 'dr1-open'], { check: true }),
            ],
        },
        {
            week: 2,
            theme: 'Wake up the support muscles',
            goal: 'Break every 40 minutes and add chin tucks and shoulder-blade squeezes.',
            why: 'With the break habit started, the snacks shift from loosening to gentle activation of the deep neck ' +
                'muscles and the muscles between the shoulder blades, the ones that help you sit tall.',
            whatToNotice: 'Look for your first drift arriving a little later in the weekly check, and your head angle sitting closer to your sit-tall baseline.',
            postureCue: 'Slide your chin straight back, then let your shoulder blades settle down and back.',
            breaks: {
                everyMin: 40,
                minutes: 3,
                note: 'Get up every 40 minutes for about 3 minutes; take the stairs or walk to refill water. Keep shifting ' +
                    'position in between. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'dr2-tuck-squeeze',
                    title: 'Tuck and squeeze',
                    exerciseIds: ['chin-tuck', 'scapular-retraction', 'shoulder-rolls'],
                    minutes: 3,
                },
                {
                    id: 'dr2-wall',
                    title: 'Wall and doorway reset',
                    exerciseIds: ['wall-angel', 'doorway-pec-stretch', 'seated-figure-four'],
                    minutes: 4,
                },
                {
                    id: 'home-dr2',
                    title: 'Evening home snack: lying neck hold',
                    exerciseIds: ['chin-tuck-supine', 'deep-neck-flexor', 'thread-the-needle'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['dr2-tuck-squeeze', 'dr2-wall']),
                day(2, ['dr2-tuck-squeeze', 'home-dr2']),
                day(3, ['dr2-tuck-squeeze', 'dr2-wall']),
                day(4, [], { rest: true }),
                day(5, ['dr2-tuck-squeeze', 'dr2-wall', 'home-dr2']),
                day(6, ['dr2-tuck-squeeze', 'dr2-wall']),
                day(7, ['dr2-tuck-squeeze', 'dr2-wall'], { check: true }),
            ],
        },
        {
            week: 3,
            theme: 'Hold it longer',
            goal: 'Break every 35 minutes and practise holding a tall position for longer.',
            why: 'Once the moves feel familiar, the useful step is holding the position: longer holds and standing ' +
                'versions ask the same muscles to keep going, which is what sitting tall through a workday needs.',
            whatToNotice: 'Your weekly check will likely show more upright minutes, with fewer drifts in the second half of the check.',
            postureCue: 'Grow tall through the crown of your head; ribs stacked over hips.',
            breaks: {
                everyMin: 35,
                minutes: 4,
                note: 'Get up every 35 minutes for about 4 minutes; use one break an hour for a snack and the others for a ' +
                    'walk. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'dr3-hold',
                    title: 'Upright hold set',
                    exerciseIds: ['upright-hold', 'seated-lat-activation', 'levator-stretch'],
                    minutes: 4,
                },
                {
                    id: 'dr3-stand',
                    title: 'Stand-up shoulder set',
                    exerciseIds: ['standing-w-retraction', 'standing-y', 'lat-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-dr3',
                    title: 'Evening home snack: lying back set',
                    exerciseIds: ['deep-neck-flexor', 'prone-ity', 'open-book'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['dr3-hold', 'dr3-stand']),
                day(2, ['dr3-hold', 'dr3-stand', 'home-dr3']),
                day(3, ['dr3-hold', 'dr3-stand']),
                day(4, [], { rest: true }),
                day(5, ['dr3-hold', 'dr3-stand', 'home-dr3']),
                day(6, ['dr3-hold', 'dr3-stand']),
                day(7, ['dr3-hold', 'dr3-stand'], { check: true }),
            ],
        },
        {
            week: 4,
            theme: 'Make it part of the day',
            goal: 'Break every 30 minutes and use short resets to sit tall through the workday.',
            why: 'The last week folds everything into the workday: a 2-minute reset you can do at the desk, a fuller ' +
                'wall set when you have time, and breaks at the 30-minute mark. The final check shows how ' +
                'your sitting holds up compared with week 1.',
            whatToNotice: 'Compare the final check with your baseline: more upright minutes, a later first drift and a head angle closer to your sit-tall baseline are the signs to look for.',
            postureCue: 'Every time you sit back down: ears over shoulders, shoulder blades down, then get back to work.',
            breaks: {
                everyMin: 30,
                minutes: 5,
                note: 'Get up every 30 minutes; a short stand-and-stretch is fine, with a 5-minute walk at least once an ' +
                    'hour. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'dr4-reset',
                    title: 'Two-minute desk reset',
                    exerciseIds: ['chin-tuck', 'scapular-retraction', 'string-overhead'],
                    minutes: 2,
                },
                {
                    id: 'dr4-wall',
                    title: 'Wall posture set',
                    exerciseIds: ['upright-hold', 'wall-angel', 'doorway-pec-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-dr4',
                    title: 'Evening home snack: floor balance set',
                    exerciseIds: ['bird-dog-chin-tuck', 'open-book', 'hip-flexor-stretch'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['dr4-reset', 'dr4-wall']),
                day(2, ['dr4-reset', 'dr4-wall', 'home-dr4']),
                day(3, ['dr4-reset', 'dr4-wall']),
                day(4, [], { rest: true }),
                day(5, ['dr4-reset', 'dr4-wall', 'home-dr4']),
                day(6, ['dr4-reset', 'dr4-wall']),
                day(7, ['dr4-reset'], { check: true }),
            ],
        },
    ],
};
// ---------------------------------------------------------------------------
// Upright Foundations — 6 weeks
// ---------------------------------------------------------------------------
const UPRIGHT_FOUNDATIONS = {
    id: 'upright-foundations-6w',
    name: 'Upright Foundations',
    weeks: 6,
    summary: 'Six weeks that build from loosening up to holding a tall posture through the whole workday.',
    forWhom: 'Desk workers who want the fuller version: enough weeks to move from the break habit to strength and then to sitting tall all day.',
    commitment: [
        'A 15-minute camera check once a week',
        'A short break every 30–45 min of sitting',
        '1–3 exercise snacks a day, 2–5 min each',
        'One rest day a week from snacks (breaks still count)',
        'Six weeks, start to finish',
    ],
    rationale: 'Desk-work guidance recommends breaking up sitting every 30–50 minutes and getting up at least once an ' +
        'hour, and suggests keeping a routine going for at least five to six weeks; this program runs six. ' +
        'Reviews of exercise snacks favour short, targeted moves in breaks over just sitting back, so each week ' +
        'pairs the break schedule with 2–5 minute snacks. Weeks 1–2 loosen up and set the break habit, weeks 3–4 ' +
        'build the deep neck and shoulder-blade muscles, weeks 5–6 practise holding a tall position through the ' +
        'workday, with a lighter final week to settle the routine. A weekly camera check shows how your sitting ' +
        'changes as you go. ' +
        STOP_LINE,
    sourceIds: [
        'dunstan-2012',
        'cornell-20-8-2',
        'exercise-snacks-2026',
        'active-break-2024',
        'waongenngarm-2021',
        'postural-shift-2023',
        'office-msd-2022',
    ],
    plan: [
        {
            week: 1,
            theme: 'Loosen up',
            goal: 'Get up every 45 minutes and learn a few easy loosening moves.',
            why: 'The first two weeks are about the break habit. Gentle range-of-motion moves keep the snacks easy so ' +
                'all your attention can go to getting up on time.',
            whatToNotice: 'Your report will likely show breaks taken on schedule. Today’s baseline check is the starting point for the whole program.',
            postureCue: 'Ears over shoulders, chin level.',
            breaks: {
                everyMin: 45,
                minutes: 2,
                note: 'Stand up and walk for 2 minutes every 45 minutes of sitting. Between breaks, shift how you sit every ' +
                    'few minutes. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'uf1-neck',
                    title: 'Neck and shoulder loosener',
                    exerciseIds: ['shoulder-rolls', 'neck-range', 'upper-trap-stretch'],
                    minutes: 3,
                },
                {
                    id: 'uf1-open',
                    title: 'Chest and hip opener',
                    exerciseIds: ['doorway-pec-stretch', 'seated-thoracic-extension', 'seated-figure-four'],
                    minutes: 4,
                },
                {
                    id: 'home-uf1',
                    title: 'Evening home snack: upper-back unwind',
                    exerciseIds: ['open-book', 'thread-the-needle'],
                    minutes: 4,
                },
            ],
            days: [
                day(1, ['uf1-neck'], { check: true }),
                day(2, ['uf1-neck', 'uf1-open']),
                day(3, ['uf1-neck', 'uf1-open']),
                day(4, [], { rest: true }),
                day(5, ['uf1-neck', 'uf1-open']),
                day(6, ['uf1-neck', 'home-uf1']),
                day(7, ['uf1-neck', 'uf1-open'], { check: true }),
            ],
        },
        {
            week: 2,
            theme: 'Open the front, free the upper back',
            goal: 'Get up every 45 minutes, add the chin tuck and stretch the chest, neck sides and upper back.',
            why: 'Long hours at a screen pull the head and shoulders forward. Stretching the front of the chest and the ' +
                'sides of the neck, and moving the upper back, makes the tall position easier to find before the ' +
                'strength weeks start.',
            whatToNotice: 'The habit should feel steadier: more breaks taken on schedule, and the weekly check may show your head angle a little closer to your sit-tall baseline.',
            postureCue: 'Slide your chin straight back, like making a double chin, and keep your eyes level.',
            breaks: {
                everyMin: 45,
                minutes: 3,
                note: 'Get up every 45 minutes for about 3 minutes; take the stairs or walk to refill water. Keep shifting ' +
                    'position in between. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'uf2-neck',
                    title: 'Chin tuck and neck stretch',
                    exerciseIds: ['chin-tuck', 'levator-stretch', 'scalene-stretch'],
                    minutes: 3,
                },
                {
                    id: 'uf2-back',
                    title: 'Upper-back and chest opener',
                    exerciseIds: ['seated-thoracic-extension', 'lat-stretch', 'doorway-pec-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-uf2',
                    title: 'Evening home snack: lying reset',
                    exerciseIds: ['chin-tuck-supine', 'open-book', 'hip-flexor-stretch'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['uf2-neck', 'uf2-back']),
                day(2, ['uf2-neck', 'uf2-back']),
                day(3, ['uf2-neck', 'home-uf2']),
                day(4, [], { rest: true }),
                day(5, ['uf2-neck', 'uf2-back']),
                day(6, ['uf2-neck', 'uf2-back', 'home-uf2']),
                day(7, ['uf2-neck', 'uf2-back'], { check: true }),
            ],
        },
        {
            week: 3,
            theme: 'Switch on the deep neck muscles',
            goal: 'Get up every 40 minutes and practise chin tucks and shoulder-blade squeezes with short holds.',
            why: 'With the front opened up, the focus moves to the small deep muscles at the front of the neck and the ' +
                'muscles between the shoulder blades. Short, precise holds teach them to switch on before they are asked to work longer.',
            whatToNotice: 'Look for your first drift arriving later in the weekly check than in week 1.',
            postureCue: 'Chin back, then shoulder blades down and back, without lifting your chin.',
            breaks: {
                everyMin: 40,
                minutes: 3,
                note: 'Get up every 40 minutes for about 3 minutes; one break an hour can be a snack, the others a walk. ' +
                    'On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'uf3-tuck',
                    title: 'Tuck and squeeze',
                    exerciseIds: ['chin-tuck', 'scapular-retraction'],
                    minutes: 2,
                },
                {
                    id: 'uf3-wall',
                    title: 'Wall shoulder set',
                    exerciseIds: ['wall-angel', 'standing-w-retraction', 'scm-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-uf3',
                    title: 'Evening home snack: lying neck and back holds',
                    exerciseIds: ['chin-tuck-supine', 'deep-neck-flexor', 'prone-ity'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['uf3-tuck', 'uf3-wall']),
                day(2, ['uf3-tuck', 'uf3-wall', 'home-uf3']),
                day(3, ['uf3-tuck', 'uf3-wall']),
                day(4, [], { rest: true }),
                day(5, ['uf3-tuck', 'uf3-wall', 'home-uf3']),
                day(6, ['uf3-tuck', 'uf3-wall']),
                day(7, ['uf3-tuck', 'uf3-wall'], { check: true }),
            ],
        },
        {
            week: 4,
            theme: 'Build strength',
            goal: 'Get up every 35 minutes and work the neck and shoulder-blade muscles a little harder.',
            why: 'Now the muscles know the job, so the moves get a little harder: standing raises, lat work and a ' +
                'resisted chin tuck at home. Strength here sets up the longer holds in weeks 5–6.',
            whatToNotice: 'Your weekly check will likely show more upright minutes than in week 2.',
            postureCue: 'Shoulder blades into your back pockets; chest open, chin level.',
            breaks: {
                everyMin: 35,
                minutes: 4,
                note: 'Get up every 35 minutes for about 4 minutes. Keep shifting position in between. On the rest day ' +
                    '(day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'uf4-seated',
                    title: 'Seated strength set',
                    exerciseIds: ['chin-tuck', 'seated-lat-activation', 'scapular-retraction'],
                    minutes: 3,
                },
                {
                    id: 'uf4-stand',
                    title: 'Standing shoulder set',
                    exerciseIds: ['standing-y', 'standing-w-retraction', 'doorway-pec-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-uf4',
                    title: 'Evening home snack: neck and back strength',
                    exerciseIds: ['deep-neck-flexor', 'chin-tuck-towel', 'prone-ity'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['uf4-seated', 'uf4-stand']),
                day(2, ['uf4-seated', 'uf4-stand', 'home-uf4']),
                day(3, ['uf4-seated', 'uf4-stand']),
                day(4, [], { rest: true }),
                day(5, ['uf4-seated', 'uf4-stand', 'home-uf4']),
                day(6, ['uf4-seated', 'uf4-stand']),
                day(7, ['uf4-seated', 'uf4-stand'], { check: true }),
            ],
        },
        {
            week: 5,
            theme: 'Hold it through the day',
            goal: 'Get up every 30 minutes and practise upright holds between tasks.',
            why: 'Sitting tall is about holding a good position for longer, not reaching it once. Upright holds and ' +
                'quick resets during the workday turn the strength from weeks 3–4 into time spent sitting tall.',
            whatToNotice: 'Your weekly check will likely show more upright minutes and fewer drifts in the second half of the check.',
            postureCue: 'Grow tall through the crown of your head; ribs stacked over hips.',
            breaks: {
                everyMin: 30,
                minutes: 5,
                note: 'Get up every 30 minutes; a short stand-and-stretch is fine, with a 5-minute walk at least once an ' +
                    'hour. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'uf5-reset',
                    title: 'Two-minute desk reset',
                    exerciseIds: ['chin-tuck', 'shoulder-rolls'],
                    minutes: 2,
                },
                {
                    id: 'uf5-hold',
                    title: 'Upright hold set',
                    exerciseIds: ['upright-hold', 'string-overhead'],
                    minutes: 3,
                },
                {
                    id: 'uf5-wall',
                    title: 'Wall posture set',
                    exerciseIds: ['wall-angel', 'standing-y', 'lat-stretch'],
                    minutes: 4,
                },
                {
                    id: 'home-uf5',
                    title: 'Evening home snack: floor balance set',
                    exerciseIds: ['bird-dog-chin-tuck', 'deep-neck-flexor', 'hamstring-stretch'],
                    minutes: 5,
                },
            ],
            days: [
                day(1, ['uf5-reset', 'uf5-hold', 'uf5-wall']),
                day(2, ['uf5-reset', 'uf5-hold', 'home-uf5']),
                day(3, ['uf5-reset', 'uf5-hold', 'uf5-wall']),
                day(4, [], { rest: true }),
                day(5, ['uf5-reset', 'uf5-hold', 'home-uf5']),
                day(6, ['uf5-reset', 'uf5-hold', 'uf5-wall']),
                day(7, ['uf5-reset', 'uf5-hold'], { check: true }),
            ],
        },
        {
            week: 6,
            theme: 'Settle the routine',
            goal: 'Keep breaks every 30 minutes with lighter snacks, then compare your final check with week 1.',
            why: 'A lighter final week lets the routine settle instead of adding more. Snacks get shorter and fewer, the ' +
                'breaks stay the same, and the final check shows how your sitting holds up compared with ' +
                'your baseline.',
            whatToNotice: 'Compare the final check with your baseline: more upright minutes, a later first drift and a head angle closer to your sit-tall baseline are the signs to look for.',
            postureCue: 'Every time you sit back down: ears over shoulders, shoulder blades down, then get back to work.',
            breaks: {
                everyMin: 30,
                minutes: 5,
                note: 'Keep getting up every 30 minutes, with a 5-minute walk at least once an hour; this is the habit to ' +
                    'carry on after the program. On the rest day (day 4) skip the snacks but keep the breaks.',
            },
            snacks: [
                {
                    id: 'uf6-hold',
                    title: 'Upright hold set',
                    exerciseIds: ['upright-hold', 'scapular-retraction', 'string-overhead'],
                    minutes: 3,
                },
                {
                    id: 'uf6-open',
                    title: 'Chest and hip opener',
                    exerciseIds: ['doorway-pec-stretch', 'seated-thoracic-extension', 'seated-figure-four'],
                    minutes: 3,
                },
                {
                    id: 'home-uf6',
                    title: 'Evening home snack: easy floor set',
                    exerciseIds: ['bird-dog-chin-tuck', 'open-book'],
                    minutes: 4,
                },
            ],
            days: [
                day(1, ['uf6-hold', 'uf6-open']),
                day(2, ['uf6-hold']),
                day(3, ['uf6-hold', 'uf6-open']),
                day(4, [], { rest: true }),
                day(5, ['uf6-hold', 'home-uf6']),
                day(6, ['uf6-hold', 'uf6-open']),
                day(7, ['uf6-hold'], { check: true }),
            ],
        },
    ],
};
export const PROGRAMS = [DESK_RESET, UPRIGHT_FOUNDATIONS];
export function programById(id) {
    return PROGRAMS.find((p) => p.id === id);
}
/** A snack anywhere in the program, by id. */
export function snackById(program, snackId) {
    for (const week of program.plan) {
        const snack = week.snacks.find((s) => s.id === snackId);
        if (snack)
            return snack;
    }
    return undefined;
}
/** Length of a planned camera check: the same for every check (see PROGRAM_CHECK_MINUTES). */
export function checkMinutes(_program, _week, _dayOfWeek) {
    return PROGRAM_CHECK_MINUTES;
}
/** Calendar days between two instants, in the device's local calendar (DST-safe). */
function localDayDiff(from, to) {
    const a = new Date(from);
    const b = new Date(to);
    const aDay = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const bDay = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((bDay - aDay) / 86_400_000);
}
/**
 * Where an enrollment is today. Days count by local calendar date from
 * `startedAt` (local midnight of day 1), so a DST change never shifts a day.
 * null before the start or for an unknown program; `finished` (pinned to the
 * last day) once the last day has passed.
 */
export function programDay(enrollment, now) {
    const program = programById(enrollment.programId);
    if (!program)
        return null;
    if (now < enrollment.startedAt)
        return null;
    const index = localDayDiff(enrollment.startedAt, now);
    if (index < 0)
        return null;
    const total = program.weeks * DAYS_PER_WEEK;
    if (index >= total)
        return { week: program.weeks, day: DAYS_PER_WEEK, finished: true };
    return {
        week: Math.floor(index / DAYS_PER_WEEK) + 1,
        day: (index % DAYS_PER_WEEK) + 1,
        finished: false,
    };
}
//# sourceMappingURL=programs.js.map